import React from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Checkbox,
  Typography,
  MenuItem,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

/**
 * Editor for BuilderIntake.customFields + hiddenFields, shared by the intake
 * Edit modal and Create form.
 *
 * Rows are edited in "UI shape": { key, label, type, optionsText, required }.
 * Use rowsToCustomFields / customFieldsToRows to convert to/from the API shape.
 */

export const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "select", label: "Dropdown" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
];

// Built-in optional fields the public form allows hiding (must match
// HIDEABLE_FIELDS in IntakeFormDynamic.js).
export const HIDEABLE_BUILTINS = [
  { name: "taxRate", label: "Tax Rate (%) / label override" },
  { name: "lot", label: "Lot Number" },
  { name: "price", label: "Sales Price ($)" },
  { name: "incentiveMax", label: "Incentive Available Max" },
  { name: "solar", label: "Solar" },
  { name: "hoa", label: "HOA ($)" },
  { name: "realtorName", label: "Realtor Name" },
  { name: "realtorPhone", label: "Realtor Phone" },
  { name: "realtorEmail", label: "Realtor Email" },
  { name: "urgency", label: "Urgency" },
];

// Payload keys the public form already uses — custom keys must not collide.
const RESERVED_KEYS = new Set([
  "slug", "leadSource", "referralSource", "firstName", "lastName",
  "phoneNumber", "emailAddress", "loanOfficer", "agentName", "agentPhone",
  "agentEmail", "community", "zip", "lot", "price", "fwdCommit", "estClose",
  "incentiveMax", "solar", "hoa", "taxRate", "realtorName", "realtorPhone",
  "realtorEmail", "additionalDetails", "urgency", "state", "customAnswers",
]);

const KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9]*$/;

export const customFieldsToRows = (customFields = []) =>
  (customFields || []).map((f) => ({
    key: f.key || "",
    label: f.label || "",
    type: f.type || "text",
    optionsText: (f.options || []).join("\n"),
    required: !!f.required,
  }));

export const rowsToCustomFields = (rows = []) =>
  rows
    .map((r) => ({
      key: String(r.key || "").trim(),
      label: String(r.label || "").trim(),
      type: r.type || "text",
      options:
        r.type === "select"
          ? String(r.optionsText || "").split("\n").map((v) => v.trim()).filter(Boolean)
          : [],
      required: !!r.required,
    }))
    .filter((r) => r.key || r.label);

const toCamelKey = (label = "") =>
  String(label)
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join("");

/** Returns an error message string, or null when all rows are valid. */
export function validateCustomFieldRows(rows = []) {
  const fields = rowsToCustomFields(rows);
  const seen = new Set();
  let error = null;
  fields.some((f) => {
    const where = f.label || f.key || "unnamed field";
    if (!f.label) {
      error = `Custom field "${where}": label is required.`;
    } else if (!f.key) {
      error = `Custom field "${where}": key is required.`;
    } else if (!KEY_PATTERN.test(f.key)) {
      error = `Custom field "${where}": key must be letters/numbers only and start with a letter (e.g. constructionStage).`;
    } else if (/date|close/i.test(f.key)) {
      error = `Custom field "${where}": key cannot contain "date" or "close" (those keys get date-formatted in the notification email). Use the Date type instead.`;
    } else if (RESERVED_KEYS.has(f.key)) {
      error = `Custom field "${where}": key "${f.key}" is reserved by the built-in form.`;
    } else if (seen.has(f.key)) {
      error = `Custom field "${where}": duplicate key "${f.key}".`;
    } else if (f.type === "select" && f.options.length === 0) {
      error = `Custom field "${where}": dropdowns need at least one option (one per line).`;
    } else {
      seen.add(f.key);
      return false;
    }
    return true;
  });
  return error;
}

export default function CustomFieldsEditor({
  rows,
  onRowsChange,
  hiddenFields,
  onHiddenFieldsChange,
}) {
  const updateRow = (idx, patch) =>
    onRowsChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeRow = (idx) => onRowsChange(rows.filter((_, i) => i !== idx));

  const addRow = () =>
    onRowsChange([...rows, { key: "", label: "", type: "text", optionsText: "", required: false }]);

  const toggleHidden = (name) =>
    onHiddenFieldsChange(
      hiddenFields.includes(name)
        ? hiddenFields.filter((f) => f !== name)
        : [...hiddenFields, name]
    );

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Custom Fields
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Extra fields shown at the bottom of this division&apos;s public form.
        </Typography>
      </Grid>

      {rows.map((row, idx) => (
        <Grid item xs={12} key={idx}>
          <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Field Label"
                  value={row.label}
                  onChange={(e) => updateRow(idx, { label: e.target.value })}
                  onBlur={() => {
                    if (!row.key && row.label) updateRow(idx, { key: toCamelKey(row.label) });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Key"
                  value={row.key}
                  onChange={(e) => updateRow(idx, { key: e.target.value })}
                  helperText="Stable ID, e.g. constructionStage"
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Type"
                  value={row.type}
                  onChange={(e) => updateRow(idx, { type: e.target.value })}
                >
                  {FIELD_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4} sm={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={row.required}
                      onChange={(e) => updateRow(idx, { required: e.target.checked })}
                    />
                  }
                  label="Required"
                />
              </Grid>
              <Grid item xs={2} sm={1} sx={{ textAlign: "right" }}>
                <IconButton aria-label="Remove field" onClick={() => removeRow(idx)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
              {row.type === "select" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    label="Dropdown Options (one per line)"
                    value={row.optionsText}
                    onChange={(e) => updateRow(idx, { optionsText: e.target.value })}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button startIcon={<AddIcon />} onClick={addRow}>
          Add Field
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Hide Built-in Fields
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Checked fields are removed from this division&apos;s public form.
        </Typography>
        <Grid container>
          {HIDEABLE_BUILTINS.map((f) => (
            <Grid item xs={12} sm={6} md={4} key={f.name}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hiddenFields.includes(f.name)}
                    onChange={() => toggleHidden(f.name)}
                  />
                }
                label={f.label}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </>
  );
}
